import { useState, type MouseEvent } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Box from '@liquidweb-dev/undercurrent/Box';
import Tooltip from '@liquidweb-dev/undercurrent/Tooltip';
import IconButton from '@liquidweb-dev/undercurrent/IconButton';
import ConstructionIcon from '@liquidweb-dev/undercurrent/icons/lucide/Construction';
import PlusIcon from '@liquidweb-dev/undercurrent/icons/lucide/Plus';
import ShieldCheckIcon from '@liquidweb-dev/undercurrent/icons/lucide/ShieldCheck';
import ShieldOffIcon from '@liquidweb-dev/undercurrent/icons/lucide/ShieldOff';
import DrawerAddServerProtection from 'components/standard/DrawerAddServerProtection';
import useAccountDetails from 'modules/queries/account/useDetails';
import sendGtmEvent from 'utility/gtm/sendGtmEvent';

interface EnhancedSecurityChipProps {
	uniqId: string;
	hostname: string;
	displayType?: string;
	hasEnhancedSecurity: boolean;
	canEnhancedSecurity: boolean;
	enhancedSecurityStatus?: string;
}

const EnhancedSecurityChip = ({
	uniqId,
	hostname,
	displayType,
	hasEnhancedSecurity,
	canEnhancedSecurity,
	enhancedSecurityStatus,
}: EnhancedSecurityChipProps) => {
	const { data: accountData } = useAccountDetails({});
	const [isDrawerOpen, setIsDrawerOpen] = useState(false);

	if (!canEnhancedSecurity) return null;

	const addOnsUrl = `/servers/${uniqId}/settings/add-ons`;
	const isSettingUp =
		hasEnhancedSecurity &&
		enhancedSecurityStatus &&
		enhancedSecurityStatus !== 'Active';

	const handleNavigateClick = (event: MouseEvent) => {
		event.stopPropagation();
	};

	const handleOpenDrawer = (event: MouseEvent) => {
		event.preventDefault();
		event.stopPropagation();
		setIsDrawerOpen(true);
	};

	const handleCloseDrawer = () => {
		setIsDrawerOpen(false);
	};

	const handleSuccess = () => {
		setIsDrawerOpen(false);
		sendGtmEvent({
			event: 'lw.event',
			eventName: 'no_security_alert_add',
			eventData: {
				account_id: accountData?.accnt,
				server_id: uniqId,
				product_type: displayType,
				timestamp: new Date().toISOString(),
				system: 'lw',
			},
		});
	};

	if (isSettingUp) {
		return (
			<Tooltip title="Setting up Security...">
				<IconButton
					variant="secondary"
					color="highlight"
					size="small"
					onClick={handleNavigateClick}
					component={RouterLink}
					to={addOnsUrl}
				>
					<ConstructionIcon />
				</IconButton>
			</Tooltip>
		);
	}

	if (hasEnhancedSecurity) {
		return (
			<Tooltip title="Advanced Security Installed">
				<IconButton
					variant="secondary"
					color="success"
					size="small"
					onClick={handleNavigateClick}
					component={RouterLink}
					to={addOnsUrl}
					aria-label="Advanced Security Installed"
				>
					<ShieldCheckIcon />
				</IconButton>
			</Tooltip>
		);
	}

	return (
		<Box>
			<Tooltip
				variant="fancy"
				arrow
				title="Advanced Security"
				message="The server is protected by baseline security controls; advanced security options are not currently applied"
				sx={{ cursor: 'pointer' }}
			>
				<IconButton
					variant="upsell"
					color="warning"
					size="small"
					onClick={handleOpenDrawer}
				>
					<>
						<PlusIcon />
						<ShieldOffIcon />
					</>
				</IconButton>
			</Tooltip>
			<Box
				onClick={(event) => {
					// Prevents clicks from bubbling up to the parent RouterLink (which would cause navigation)
					event.stopPropagation();
				}}
			>
				<DrawerAddServerProtection
					uniqId={uniqId}
					hostname={hostname}
					open={isDrawerOpen}
					onClose={handleCloseDrawer}
					onSuccess={handleSuccess}
				/>
			</Box>
		</Box>
	);
};

export default EnhancedSecurityChip;
